import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, Users, Heart, Award, Target, Eye, Star } from 'lucide-react';
import { apiService } from '../services/apiService';

const About = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNosotros = async () => {
      try {
        const response = await apiService.getNosotros();
        if (response.estado === 1) {
          setData(response.data);
        }
      } catch (error) {
        console.error('Error cargando datos de nosotros:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNosotros();
  }, []);

  // Valores corporativos
  const valores = [
    {
      icon: CheckCircle,
      titulo: 'Calidad',
      descripcion: 'Equipos de primera línea',
      color: 'from-yellow-400 to-yellow-600'
    },
    {
      icon: Clock,
      titulo: 'Puntualidad',
      descripcion: 'Cumplimos con los tiempos',
      color: 'from-yellow-400 to-yellow-600'
    },
    {
      icon: Users,
      titulo: 'Equipo',
      descripcion: 'Profesionales capacitados',
      color: 'from-yellow-400 to-yellow-600'
    },
    {
      icon: Heart,
      titulo: 'Pasión',
      descripcion: 'Amamos lo que hacemos',
      color: 'from-yellow-400 to-yellow-600'
    }
  ];

  if (loading) {
    return (
      <section className="min-h-screen bg-black flex items-center justify-center pt-20">
        <motion.div 
          className="w-16 h-16 border-4 border-yellow-600/30 border-t-yellow-500 rounded-full" 
          animate={{ rotate: 360 }} 
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }} 
        />
      </section>
    );
  }

  if (!data) {
    return (
      <section className="min-h-screen bg-black flex items-center justify-center pt-20">
        <p className="text-gray-400 text-xl">No se pudieron cargar los datos</p>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-black text-white pt-24 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* --- SECCIÓN PRINCIPAL CON IMAGEN Y CONTENIDO --- */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mb-20">
          
          {/* Columna Izquierda: Imagen */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative w-full h-[500px] lg:h-[600px] rounded-2xl overflow-hidden border border-yellow-600/20 bg-neutral-900/30">
              {data.imagen_url ? (
                <img 
                  src={data.imagen_url} 
                  alt={data.titulo} 
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-yellow-900/30 to-black flex items-center justify-center">
                  <Award className="w-24 h-24 text-yellow-600/40" />
                </div>
              )}
              
              {/* Overlay decorativo - solo en la parte inferior */}
              <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              
              {/* Badge decorativo */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-black/70 backdrop-blur-md border border-yellow-600/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center">
                      <Star className="w-6 h-6 text-black" fill="black" />
                    </div>
                    <div>
                      <p className="text-yellow-500 font-bold text-lg">Excelencia Garantizada</p>
                      <p className="text-gray-400 text-sm">+10 años de experiencia</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Columna Derecha: Contenido */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col justify-center"
          >
            {/* Título */}
            <div className="mb-8">
              <span className="inline-block text-yellow-500 text-sm font-semibold tracking-widest uppercase mb-3 border-b border-yellow-600/50 pb-1">
                Sobre Nosotros
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                {data.titulo}
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent rounded-full" />
            </div>

            {/* Contenido principal */}
            <div 
              className="prose prose-invert prose-lg max-w-none mb-8 text-gray-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: data.contenido }}
            />

            {/* Stats rápidos */}
            <div className="grid grid-cols-3 gap-6 mt-8 pt-8 border-t border-yellow-600/20">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-500 mb-1">500+</div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">Eventos</div>
              </div>
              <div className="text-center border-l border-yellow-600/20">
                <div className="text-3xl font-bold text-yellow-500 mb-1">10+</div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">Años</div>
              </div>
              <div className="text-center border-l border-yellow-600/20">
                <div className="text-3xl font-bold text-yellow-500 mb-1">99%</div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">Satisfacción</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* --- MISIÓN Y VISIÓN --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {/* Misión */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="bg-neutral-900/40 border border-yellow-600/20 rounded-2xl p-8 hover:border-yellow-500/40 transition-colors duration-500"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 border border-yellow-600/30 flex items-center justify-center flex-shrink-0">
                <Target className="w-7 h-7 text-yellow-500" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-3">Misión</h3>
                <p className="text-gray-400 leading-relaxed">
                  {data.mision}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Visión */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="bg-neutral-900/40 border border-yellow-600/20 rounded-2xl p-8 hover:border-yellow-500/40 transition-colors duration-500"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 border border-yellow-600/30 flex items-center justify-center flex-shrink-0">
                <Eye className="w-7 h-7 text-yellow-500" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-3">Visión</h3>
                <p className="text-gray-400 leading-relaxed">
                  {data.vision}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* --- NUESTROS VALORES --- */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="text-center mb-12">
            <span className="text-yellow-500 text-sm font-semibold tracking-widest uppercase border-b border-yellow-600/50 pb-1">
              Nuestros Valores
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {valores.map((valor, index) => {
              const Icon = valor.icon;
              return (
                <motion.div
                  key={valor.titulo}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ y: -8 }}
                  className="group bg-neutral-900/40 border border-yellow-600/20 rounded-2xl p-8 text-center hover:border-yellow-500/50 transition-all duration-500 hover:shadow-[0_10px_40px_-15px_rgba(234,179,8,0.15)]"
                >
                  {/* Icono */}
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 border border-yellow-600/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-yellow-500" />
                  </div>
                  
                  {/* Título */}
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-500 transition-colors">
                    {valor.titulo}
                  </h3>
                  
                  {/* Descripción */}
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {valor.descripcion}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default About;